/**
 * HID Global + Suprema access-control catalog — readers + controllers.
 *
 * All specs verified against hidglobal.com/products/* and
 * supremainc.com/en/hardware/* on 2026-05-15. URL cited per entry.
 *
 * The schema's `AccessReaderSpec.technology` enum only supports
 * 'em-marin-125khz' | 'mifare' | 'dallas-ibutton' | 'wiegand-only'.
 * Legacy HID Prox-only readers map to 'wiegand-only'; iCLASS SE
 * (13.56 MHz only) maps to 'mifare'; multiCLASS SE dual-frequency
 * maps to both 'mifare' + 'em-marin-125khz'.
 *
 * Biometric reader extras (Suprema face/fingerprint) are described in
 * `tagline` since the schema's technology enum is RFID-focused.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HID_SUPREMA_ACCESS_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // HID Global — Signo readers (current flagship, mobile + OSDP)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'hid-signo-20',
    manufacturer: 'HID Global',
    model: 'Signo 20',
    category: 'access-reader',
    tagline: 'Mullion multi-tech reader with OSDP + Wiegand, Bluetooth/NFC mobile access',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/20',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP65',
    },
  },

  {
    id: 'hid-signo-20k',
    manufacturer: 'HID Global',
    model: 'Signo 20K',
    category: 'access-reader',
    tagline: 'Mullion keypad reader with OSDP + Wiegand, mobile credentials',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/20k',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP65',
    },
  },

  {
    id: 'hid-signo-40',
    manufacturer: 'HID Global',
    model: 'Signo 40',
    category: 'access-reader',
    tagline: 'Wall-switch multi-tech reader with OSDP + Wiegand, Bluetooth/NFC',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/40',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP65',
    },
  },

  {
    id: 'hid-signo-40k',
    manufacturer: 'HID Global',
    model: 'Signo 40K',
    category: 'access-reader',
    tagline: 'Wall-switch keypad reader with OSDP + Wiegand, mobile-ready',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/40k',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP65',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // HID Global — iCLASS SE readers (13.56 MHz smart-card)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'hid-iclass-se-r10',
    manufacturer: 'HID Global',
    model: 'iCLASS SE R10',
    category: 'access-reader',
    tagline: 'Mini-mullion 13.56 MHz smart card reader, OSDP or Wiegand',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/r10',
    specs: {
      technology: ['mifare'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: false,
      keypad: false,
    },
  },

  {
    id: 'hid-iclass-se-r15',
    manufacturer: 'HID Global',
    model: 'iCLASS SE R15',
    category: 'access-reader',
    tagline: 'Mullion 13.56 MHz smart card reader, OSDP or Wiegand',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/r15',
    specs: {
      technology: ['mifare'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: false,
      keypad: false,
    },
  },

  {
    id: 'hid-iclass-se-r40',
    manufacturer: 'HID Global',
    model: 'iCLASS SE R40',
    category: 'access-reader',
    tagline: 'Wall-switch 13.56 MHz smart card reader, OSDP or Wiegand',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/r40',
    specs: {
      technology: ['mifare'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: false,
      keypad: false,
    },
  },

  {
    id: 'hid-iclass-se-rk40',
    manufacturer: 'HID Global',
    model: 'iCLASS SE RK40',
    category: 'access-reader',
    tagline: 'Wall-switch 13.56 MHz smart card reader with keypad, OSDP or Wiegand',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/rk40',
    specs: {
      technology: ['mifare'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: false,
      keypad: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // HID Global — multiCLASS SE (dual-frequency)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'hid-multiclass-se-rp15',
    manufacturer: 'HID Global',
    model: 'multiCLASS SE RP15',
    category: 'access-reader',
    tagline: 'Mullion dual-frequency 125 kHz + 13.56 MHz reader, OSDP or Wiegand',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/rp15',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP55',
    },
  },

  {
    id: 'hid-multiclass-se-rp40',
    manufacturer: 'HID Global',
    model: 'multiCLASS SE RP40',
    category: 'access-reader',
    tagline: 'Wall-switch dual-frequency 125 kHz + 13.56 MHz reader, OSDP or Wiegand',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/rp40',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: false,
      keypad: false,
    },
  },

  {
    id: 'hid-multiclass-se-rpk40',
    manufacturer: 'HID Global',
    model: 'multiCLASS SE RPK40',
    category: 'access-reader',
    tagline: 'Wall-switch dual-frequency reader with keypad for multi-factor auth',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/rpk40',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: false,
      keypad: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // HID Global — Legacy 125 kHz Prox (still shipping for retrofit)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'hid-proxpoint-plus-6005',
    manufacturer: 'HID Global',
    model: 'ProxPoint Plus 6005',
    category: 'access-reader',
    tagline: 'Mini-mullion 125 kHz HID Prox reader, Wiegand legacy retrofit',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/6005',
    specs: {
      technology: ['wiegand-only'],
      protocol: ['wiegand-26'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP55',
    },
  },

  {
    id: 'hid-proxpro-ii-5455',
    manufacturer: 'HID Global',
    model: 'ProxPro II 5455',
    category: 'access-reader',
    tagline: 'Wall-switch 125 kHz HID Prox reader with up to 22 cm read range',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/5455',
    specs: {
      technology: ['wiegand-only'],
      protocol: ['wiegand-26'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP55',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // HID Global — Aero controller (Mercury-based)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'hid-aero-x1100',
    manufacturer: 'HID Global',
    model: 'Aero X1100',
    category: 'door-controller',
    tagline: 'Mercury-based intelligent IP controller, 2 doors onboard, expandable to 64',
    verified: true,
    datasheetUrl: 'https://www.hidglobal.com/products/x1100',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Suprema — BioEntry / BioLite (fingerprint terminals)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'suprema-bioentry-w2',
    manufacturer: 'Suprema',
    model: 'BioEntry W2',
    category: 'access-reader',
    tagline: 'Rugged outdoor fingerprint reader with dual-freq RFID, TCP/IP + Wiegand + OSDP V2',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/compact-outdoor-fingerprint-device-bioentry-w2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP67',
    },
  },

  {
    id: 'suprema-bioentry-w3',
    manufacturer: 'Suprema',
    model: 'BioEntry W3',
    category: 'access-reader',
    tagline: 'AI facial authentication mullion reader with Template-on-Mobile, OSDP V2 + Wiegand',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/ai-powered-facial-authentication-device-bioentry-w3.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
    },
  },

  {
    id: 'suprema-bioentry-p2',
    manufacturer: 'Suprema',
    model: 'BioEntry P2',
    category: 'access-reader',
    tagline: 'Compact mullion IP fingerprint reader with dual-freq RFID and Wiegand',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/compact-fingerprint-device-bioentry-p2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP65',
    },
  },

  {
    id: 'suprema-biolite-n2',
    manufacturer: 'Suprema',
    model: 'BioLite N2',
    category: 'access-reader',
    tagline: 'Outdoor fingerprint terminal with keypad and dual-freq RFID, TCP/IP + Wiegand',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/outdoor-fingerprint-terminal-biolite-n2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP67',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Suprema — BioStation / FaceStation (touchscreen terminals)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'suprema-biostation-2a',
    manufacturer: 'Suprema',
    model: 'BioStation 2a',
    category: 'access-reader',
    tagline: 'Fingerprint terminal with OSDP V2, Wiegand, PoE, 500k user capacity',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/outdoor-fingerprint-terminal-biostation-2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: false,
      keypad: true,
    },
  },

  {
    id: 'suprema-biostation-3',
    manufacturer: 'Suprema',
    model: 'BioStation 3',
    category: 'access-reader',
    tagline: 'Face + fingerprint + QR + mobile + RFID + PIN terminal, PoE+, IP65/IK06',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/new-door-access-experience-biostation3.asp',
    specs: {
      technology: ['mifare'],
      protocol: ['wiegand-26'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP65',
    },
  },

  {
    id: 'suprema-biostation-a2',
    manufacturer: 'Suprema',
    model: 'BioStation A2',
    category: 'access-reader',
    tagline: '5-inch touchscreen fingerprint terminal, TCP/IP + RS-485 + Wiegand, PoE, WiFi',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/fingerprint-terminal-biostation-a2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP65',
    },
  },

  {
    id: 'suprema-facestation-f2',
    manufacturer: 'Suprema',
    model: 'FaceStation F2',
    category: 'access-reader',
    tagline: 'Fusion face + fingerprint multimodal terminal with 7-inch LCD, IP65, OSDP',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/fusion-multimodal-terminal-facestation-f2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: true,
      ipRating: 'IP65',
    },
  },

  {
    id: 'suprema-facelite',
    manufacturer: 'Suprema',
    model: 'FaceLite',
    category: 'access-reader',
    tagline: 'Compact indoor face-recognition terminal with dual-freq RFID + Wiegand',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/compact-face-recognition-terminal-facelite.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26'],
      outdoor: false,
      keypad: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Suprema — XPass RFID readers
  // ─────────────────────────────────────────────────────────────

  {
    id: 'suprema-xpass-d2',
    manufacturer: 'Suprema',
    model: 'XPass D2',
    category: 'access-reader',
    tagline: 'Outdoor mullion dual-freq RFID reader with OSDP-verified secure channel',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/compact-outdoor-rfid-reader-xpass-d2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP67',
    },
  },

  {
    id: 'suprema-xpass-2',
    manufacturer: 'Suprema',
    model: 'XPass 2',
    category: 'access-reader',
    tagline: 'Outdoor RFID reader with PoE, NFC/BLE mobile access, OSDP V2 compliant',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/outdoor-compact-rfid-device-xpass-2.asp',
    specs: {
      technology: ['mifare', 'em-marin-125khz'],
      protocol: ['wiegand-26', 'osdp'],
      outdoor: true,
      keypad: false,
      ipRating: 'IP67',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Suprema — CoreStation controllers
  // ─────────────────────────────────────────────────────────────

  {
    id: 'suprema-corestation',
    manufacturer: 'Suprema',
    model: 'CoreStation',
    category: 'door-controller',
    tagline: 'Intelligent biometric controller with RS-485 (OSDP) + Wiegand, expandable to 4 doors',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/biometric-access-controller-corestation.asp',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },

  {
    id: 'suprema-corestation-20',
    manufacturer: 'Suprema',
    model: 'CoreStation 20',
    category: 'door-controller',
    tagline: '2-door BioStar 2 IP controller, RFID-only, up to 500,000 users, PoE+ on CS-20P',
    verified: true,
    datasheetUrl: 'https://www.supremainc.com/en/hardware/2door-controller-corestation20.asp',
    specs: {
      doors: 2,
      hostBus: 'tcp-ip',
    },
  },
];
